'use client';

import Button from '@/ui/components/shared/Button';
import { useCartStore } from '@/ui/store/cart';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLongLeftIcon } from '@heroicons/react/24/outline';
import { parsePriceVariant } from '@/ui/utils/parsePriceVariant';
import { generateRandomId } from '@/ui/utils/helpers';
import { useTranslations } from 'next-intl';
import { CartUpsellingItem } from './CartUpsellingItemSection/types/CartUpsellingItem';
import { AddedToCart } from '../AddedToCartBar';
import { CartUpsellingItemSection } from './CartUpsellingItemSection/types/CartUpsellingItemSection';
import CartAddOnSection from './CartUpsellingItemSection';

interface CartAddOnServicesProps {
  addOnSections: CartUpsellingItemSection[];
}

function CartAddOnServices({ addOnSections }: CartAddOnServicesProps) {
  const t = useTranslations('CartAddOnServices');

  // Extract all items from sections and initialize them as selected
  // const allItems = addOnSections.map((section) => section.item);
  const [selectedItems, setSelectedItems] = useState<CartUpsellingItem[]>([]);

  const {
    addCartItem,
    setShowCartUpselling,
    toggleCartDisplay,
    lastAddedToCart,
    setAdditionalInformationTiles,
    setShowAdditionalInformation,
    getShowAdditionalInformation,
    activeAdditionalInfoBoxId,
    setActiveAdditionalInfoBoxId,
  } = useCartStore();

  useEffect(() => {
    const handlePopState = () => {
      if (getShowAdditionalInformation()) {
        setShowAdditionalInformation(false);
        setActiveAdditionalInfoBoxId(undefined);
      }
    };
    globalThis.addEventListener('popstate', handlePopState);

    return () => {
      globalThis.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const toggleSelection = (itemToToggle: CartUpsellingItem) => {
    setSelectedItems((prevSelectedItems) =>
      prevSelectedItems.some((item) => item.sku === itemToToggle.sku)
        ? prevSelectedItems.filter((item) => item.sku !== itemToToggle.sku)
        : [...prevSelectedItems, itemToToggle],
    );
  };

  const addToCart = () => {
    selectedItems.forEach((item) => {
      const validPriceVariant = parsePriceVariant(item.priceVariant);
      if (!validPriceVariant) {
        return;
      }
      addCartItem({
        id: item.id,
        cartItemId: generateRandomId(),
        shape: item.shape,
        name: item.name,
        image: item.image,
        priceVariant: validPriceVariant,
        quantity: 1,
        sku: item.sku,
        url: item.path,
        subscription: item.subscription
          ? {
              subscriptionPlanId: item.subscription.planId,
              period: item.subscription.planPeriod,
            }
          : undefined,
      });
    });
    setShowCartUpselling(false);
  };

  const setAndDisplayAdditionalInformation = (
    display: boolean,
    addOnSection?: CartUpsellingItemSection,
    boxId?: string,
  ) => {
    setAdditionalInformationTiles(addOnSection?.additionalInformationTiles);
    setShowAdditionalInformation(display);
    setActiveAdditionalInfoBoxId(display ? boxId : undefined);
    if (display && globalThis.location.hash !== '#additional-information') {
      globalThis.history.pushState(
        {},
        '',
        `${globalThis.location.pathname}${globalThis.location.search}#additional-information`,
      );
    } else if (!display && globalThis.location.hash === '#additional-information') {
      globalThis.history.replaceState(
        {},
        '',
        `${globalThis.location.pathname}${globalThis.location.search}`,
      );
    }
  };

  const buttonAction = () => {
    selectedItems.length > 0 ? addToCart() : setShowCartUpselling(false);
    setShowAdditionalInformation(false);
    setActiveAdditionalInfoBoxId(undefined);
    if (globalThis.location.hash === '#additional-information') {
      globalThis.history.replaceState(
        {},
        '',
        `${globalThis.location.pathname}${globalThis.location.search}`,
      );
    }
  };

  const backToChooser = () => {
    // Close the cross-sell drawer to reveal the still-mounted variant chooser (the inverse of the
    // toggleCartDisplay() that add-to-cart triggered). The already-added product stays in the cart.
    setShowAdditionalInformation(false);
    setActiveAdditionalInfoBoxId(undefined);
    toggleCartDisplay();
  };

  return (
    addOnSections && (
      <div className="flex h-full flex-col">
        {/* The chooser behind is inert while this drawer is open, freezing its top-left Back.
            Render our own Back into document.body (non-inert, on top) so it stays clickable and
            routes to the immediate previous step — the variant chooser. */}
        {typeof document !== 'undefined' &&
          createPortal(
            <button
              type="button"
              data-testid="upselling-top-left-back"
              onClick={backToChooser}
              className="fixed left-4 top-3 z-[70] flex items-center gap-1 sm:left-6"
            >
              <ArrowLongLeftIcon className="h-5 w-5 text-primary-100" />
              {t('back')}
            </button>,
            document.body,
          )}

        <div className="flex-1 overflow-auto p-6">
          {lastAddedToCart && <AddedToCart productName={lastAddedToCart.name} />}

          <div className="">
            {addOnSections.map((section, index) => (
              <CartAddOnSection
                key={`${section.item.id}-${index}`}
                section={section}
                selectedItems={selectedItems}
                toggleSelection={toggleSelection}
                setAndDisplayAdditionalInformation={setAndDisplayAdditionalInformation}
                activeBoxId={activeAdditionalInfoBoxId}
              />
            ))}
          </div>
        </div>
        <div className="sticky bottom-0 left-0 w-full bg-white px-10 py-2 shadow-nuki-blured">
          <div className="flex w-full items-center justify-between gap-3">
            <Button type="button" variant="secondary" onClick={backToChooser} className="px-6">
              {t('back')}
            </Button>
            <Button type="button" onClick={buttonAction} className="px-6">
              {selectedItems.length > 0 ? t('ADD TO CART') : t('Skip')}
            </Button>
          </div>
        </div>
      </div>
    )
  );
}

export default CartAddOnServices;
